/**
 * Every decision `/stammdaten` makes, and every mutation it APPLIES.
 *
 * The same split as lib/staffView.ts, for the same reason (that file's own
 * docblock, the STANDING RULE from the Task 7 and Task 8 reviews): pinning a
 * DECISION is not enough, because both reviews' escapes were planted at CALL
 * SITES, not in decision tables. So two layers live here:
 *
 *  1. THE DECISIONS: which section a URL fragment names, how the settings
 *     registry's flat key list splits into "Firma" and "DATEV", what a
 *     server error code says in German. Pure, exhaustively tested in Node
 *     (test/masterDataView.test.ts).
 *
 *  2. THE ORCHESTRATION: `*Action` functions that take their collaborators as
 *     parameters -- the API call, the confirm, the state setters, the
 *     QueryClient -- so the code that CHOOSES WHICH ROW TO ACT ON is callable
 *     from a Node test with no renderer. `runStaffMutation` (lib/staffView.ts)
 *     is the shape copied here, as `runMasterDataMutation`.
 *
 * THE THREE CONCRETE RISKS ON THIS VIEW (the controller's own list): a
 * rename/delete applied to a different row than the one selected, a reorder
 * that persists an order different from the one shown, and a feature toggle
 * that writes the opposite value or targets the wrong flag. Every `*Action`
 * below takes the ROW (or the FLAG and the VALUE) as an explicit argument,
 * never re-derived from ambient state, so a call-site swap is a wrong
 * argument a Node test can catch. The three that only a render can prove
 * (which row a button is actually bound to) are asserted in DOM suite #1.
 *
 * NONE OF THIS IS AUTHORIZATION (capabilities.ts's own docblock, and
 * staffView.ts's). Every route here is `adminGuards('manager')`
 * (api/src/adminRoutes.ts) -- manager and owner alike -- so unlike the Staff
 * view there is no per-section role gate to hide here at all.
 */
import type { QueryClient } from '@tanstack/react-query';
import { ApiError } from './api';
import { t } from './i18n';
import { moveItem } from './reorder';
import type { MasterDataTab } from './router';
import { errorMessage } from '../components/ErrorNotice';
import { invalidateMutation, queryKeys, reportAuthFailure } from '../queries';
import type { FeaturesResponse, ListEntry, Location, SettingsResponse } from './types';

// ── tabs ─────────────────────────────────────────────────────────────────────

/** Literal t() calls, one per tab: see the note in components/Blocking.tsx. */
export function masterDataTabLabel(tab: MasterDataTab): string {
  switch (tab) {
    case 'locations': return t('masterData.tab.locations');
    case 'lists': return t('masterData.tab.lists');
    case 'settings': return t('masterData.tab.settings');
  }
}

// ── shared mutation shell ────────────────────────────────────────────────────

/** The three collaborators every mutation on this view needs. Passed in,
 *  never reached for, so a Node test can hold all three (staffView.ts's own
 *  `MutationShell`, copied). */
export interface MutationShell {
  queryClient: QueryClient;
  setBusy: (busy: boolean) => void;
  setError: (message: string | null) => void;
}

export interface RunMasterDataMutationOptions<R> {
  onSuccess?: (result: R) => void;
  /** Defaults to the generic `error.<code>` resolver (components/ErrorNotice.ts).
   *  Overridden where a code is reused across an UNRELATED resource elsewhere
   *  in this SPA -- see `listErrorMessage`'s docblock for the concrete case. */
  resolveError?: (error: unknown) => string;
}

/**
 * One mutation, applied: busy on, previous error cleared, run, invalidate
 * THROUGH THE SHARED MAP (queries.ts's `INVALIDATES`), report. The same
 * contract as `runStaffMutation`, parametrised on the error resolver because
 * this single view spans four resources whose error vocabularies collide
 * (see `listErrorMessage`).
 */
export async function runMasterDataMutation<R>(
  mutation: string,
  run: () => Promise<R>,
  shell: MutationShell,
  options: RunMasterDataMutationOptions<R> = {},
): Promise<boolean> {
  shell.setBusy(true);
  shell.setError(null);
  try {
    const result = await run();
    invalidateMutation(shell.queryClient, mutation);
    options.onSuccess?.(result);
    return true;
  } catch (error) {
    // A 401 drives the SESSION, not just this panel's error line (final review
    // I-1), and it happens before the resolver runs so no per-resource error
    // vocabulary can shadow it.
    reportAuthFailure(shell.queryClient, error);
    shell.setError((options.resolveError ?? errorMessage)(error));
    return false;
  } finally {
    // Always, success or failure: a form left permanently disabled after one
    // failed write is indistinguishable from a hung app.
    shell.setBusy(false);
  }
}

// ── error copy ───────────────────────────────────────────────────────────────

/**
 * `already_assigned`'s PAYLOAD IS THE POINT (task-10-brief.md Step 1): "gehört
 * bereits zu C1-89-A-3" is actionable, "Konflikt" is not. Every other code
 * falls through to the generic `error.<code>` resolver.
 */
export function locationErrorMessage(error: unknown): string {
  if (error instanceof ApiError && error.code === 'already_assigned') {
    const existing = typeof error.body.existingStandort === 'string' ? error.body.existingStandort : '';
    return t('error.already_assigned', { existing });
  }
  return errorMessage(error);
}

/**
 * `name_taken` IS NOT THIS VIEW'S OWN CODE. api/src/adminRoutes.ts answers the
 * identical `409 name_taken` for a duplicate STAFF display name (mirrored in
 * staffView.ts's `staffErrorMessage`, key `error.name_taken`, whose copy
 * explains the scanner's name+PIN login) and for a duplicate LIST name --
 * two unrelated resources sharing one error code. The generic `error.<code>`
 * resolver is GLOBAL (lib/api.ts's own convention), so falling through here
 * would render the STAFF explanation on a list rename. Intercepted before
 * that fallback, with its own key.
 */
export function listErrorMessage(error: unknown): string {
  if (error instanceof ApiError && error.code === 'name_taken') return t('error.list_name_taken');
  return errorMessage(error);
}

/**
 * `unknown_setting_key`/`invalid_value`/`value_too_long` (api/src/adminRoutes.ts's
 * `PUT /settings`). In ordinary use these never reach the browser: the form
 * writes only through `changedSettings`, which only ever emits keys the
 * server's own `keys` list named. A code here means the client's copy of that
 * list has drifted -- "loud", per task-10-brief.md Step 3 -- so it is still
 * given real copy, literal `t()` calls and all, the same discipline
 * staffView.ts's `staffErrorMessage` applies even to codes that need no
 * interpolation.
 */
export function settingsErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    switch (error.code) {
      case 'unknown_setting_key': return t('error.unknown_setting_key');
      case 'invalid_value': return t('error.invalid_value');
      case 'value_too_long': return t('error.value_too_long');
    }
  }
  return errorMessage(error);
}

/**
 * `not_self_serve`/`unknown_flag` (api/src/adminRoutes.ts's `PATCH
 * /features`). `not_self_serve` should be UNREACHABLE from this view --
 * `toggleSelfServeFeatureAction`'s own type only accepts `'ebay' |
 * 'priceScan'`, so there is no call site that could ask for `photos` or
 * `enrichment` -- but the server refuses them rather than ignoring them
 * (task-10-brief.md Step 4), so this is the honest answer if that type ever
 * gets bypassed, not a silent "Der Vorgang ist fehlgeschlagen."
 */
export function featuresErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    switch (error.code) {
      case 'not_self_serve': return t('error.not_self_serve');
      case 'unknown_flag': return t('error.unknown_flag');
      case 'invalid_value': return t('error.invalid_value');
    }
  }
  return errorMessage(error);
}

// ── locations ────────────────────────────────────────────────────────────────

export function locationBarcodeLabel(location: Location): string {
  return location.barcode ?? t('masterData.locations.noBarcode');
}

export interface LocationDraft { name: string; barcode: string }

export interface CreateLocationIO extends MutationShell {
  createLocation: (json: { name: string; barcode?: string | null }) => Promise<unknown>;
  onCreated: () => void;
}

export async function createLocationAction(draft: LocationDraft, io: CreateLocationIO): Promise<boolean> {
  const name = draft.name.trim();
  if (!name) {
    io.setError(t('error.name_required'));
    return false;
  }
  const barcode = draft.barcode.trim();
  return runMasterDataMutation(
    'createLocation',
    () => io.createLocation({ name, barcode: barcode === '' ? null : barcode }),
    io,
    { onSuccess: () => io.onCreated(), resolveError: locationErrorMessage },
  );
}

export interface UpdateLocationIO extends MutationShell {
  updateLocation: (id: string, json: { name?: string; barcode?: string | null }) => Promise<unknown>;
}

export interface RenameLocationIO extends UpdateLocationIO {
  onRenamed: () => void;
}

/**
 * PATCHes `{ name }` ONLY -- never `barcode`, touched or not. `api/src/
 * adminRoutes.ts:544-545` leaves an OMITTED `barcode` alone; a rename call
 * site that also sent the row's current barcode back would be harmless today
 * and a silent clobber the day a concurrent edit lands between the row being
 * read and this PATCH being sent. There is nothing here to get wrong because
 * there is nothing here to send but the one field the operator changed.
 */
export async function renameLocationAction(
  location: Location,
  name: string,
  io: RenameLocationIO,
): Promise<boolean> {
  const trimmed = name.trim();
  if (!trimmed) {
    io.setError(t('error.name_required'));
    return false;
  }
  if (trimmed === location.name) return false; // no-op: nothing changed to write
  return runMasterDataMutation(
    'updateLocation',
    () => io.updateLocation(location.id, { name: trimmed }),
    io,
    { onSuccess: () => io.onRenamed(), resolveError: locationErrorMessage },
  );
}

export interface SetLocationBarcodeIO extends UpdateLocationIO {
  onSaved: () => void;
}

/**
 * PATCHes `{ barcode: <the typed value> }` ONLY -- never `name`. Sends a
 * NON-EMPTY string always: an empty submission here would be indistinguishable
 * from "leave it alone" at the server (`barcode !== undefined` is the whole
 * test), so an empty value is refused client-side and `clearLocationBarcodeAction`
 * is the explicit, separate action for `barcode: null`. This is what makes
 * "untouched" and "emptied" two different call sites instead of one
 * ambiguous string (task-10-brief.md Step 1's own framing).
 */
export async function setLocationBarcodeAction(
  location: Location,
  barcode: string,
  io: SetLocationBarcodeIO,
): Promise<boolean> {
  const trimmed = barcode.trim();
  if (!trimmed) {
    io.setError(t('error.barcode_required'));
    return false;
  }
  return runMasterDataMutation(
    'updateLocation',
    () => io.updateLocation(location.id, { barcode: trimmed }),
    io,
    { onSuccess: () => io.onSaved(), resolveError: locationErrorMessage },
  );
}

/** PATCHes `{ barcode: null }` -- the one shape that actually clears the
 *  field server-side. A row with no barcode already has nothing to clear. */
export async function clearLocationBarcodeAction(location: Location, io: UpdateLocationIO): Promise<boolean> {
  if (location.barcode === null) return false;
  return runMasterDataMutation(
    'updateLocation',
    () => io.updateLocation(location.id, { barcode: null }),
    io,
    { resolveError: locationErrorMessage },
  );
}

export interface DeleteLocationIO extends MutationShell {
  deleteLocation: (id: string) => Promise<unknown>;
  /** window.confirm at the call site; a function here so the confirmation
   *  TEXT -- which names both the row and what deleting does NOT undo -- is
   *  assertable without a browser. */
  confirm: (message: string) => boolean;
}

/**
 * Deletion is immediate and names what it does not undo: the merged writers
 * mirror D1 to the sheet tabs, and a deleted location does not un-write
 * history (task-10-brief.md Step 1). The confirmation is the one place an
 * operator reads that before it happens.
 */
export async function deleteLocationAction(location: Location, io: DeleteLocationIO): Promise<boolean> {
  if (!io.confirm(t('masterData.locations.confirmDelete', { name: location.name }))) return false;
  return runMasterDataMutation(
    'deleteLocation',
    () => io.deleteLocation(location.id),
    io,
    { resolveError: locationErrorMessage },
  );
}

// ── lists ────────────────────────────────────────────────────────────────────

export interface CreateListIO extends MutationShell {
  createList: (json: { name: string }) => Promise<unknown>;
  onCreated: () => void;
}

export async function createListAction(name: string, io: CreateListIO): Promise<boolean> {
  const trimmed = name.trim();
  if (!trimmed) {
    io.setError(t('error.name_required'));
    return false;
  }
  return runMasterDataMutation(
    'createList',
    () => io.createList({ name: trimmed }),
    io,
    { onSuccess: () => io.onCreated(), resolveError: listErrorMessage },
  );
}

export interface RenameListIO extends MutationShell {
  renameList: (id: string, json: { name: string }) => Promise<unknown>;
  onRenamed: () => void;
}

export async function renameListAction(list: ListEntry, name: string, io: RenameListIO): Promise<boolean> {
  const trimmed = name.trim();
  if (!trimmed) {
    io.setError(t('error.name_required'));
    return false;
  }
  if (trimmed === list.name) return false;
  return runMasterDataMutation(
    'renameList',
    () => io.renameList(list.id, { name: trimmed }),
    io,
    { onSuccess: () => io.onRenamed(), resolveError: listErrorMessage },
  );
}

export interface DeleteListIO extends MutationShell {
  deleteList: (id: string) => Promise<unknown>;
  confirm: (message: string) => boolean;
}

export async function deleteListAction(list: ListEntry, io: DeleteListIO): Promise<boolean> {
  if (!io.confirm(t('masterData.lists.confirmDelete', { name: list.name }))) return false;
  return runMasterDataMutation(
    'deleteList',
    () => io.deleteList(list.id),
    io,
    { resolveError: listErrorMessage },
  );
}

// ── lists: reorder ───────────────────────────────────────────────────────────

export interface ReorderListsIO {
  queryClient: QueryClient;
  reorderLists: (order: string[]) => Promise<{ lists: ListEntry[] }>;
  setError: (message: string | null) => void;
}

/**
 * THE ORCHESTRATION HALF OF `moveItem` -- pinning that the pure permutation is
 * correct (test/reorder.test.ts) is not the same as pinning that the view
 * PERSISTS exactly what it computed (the controller's own instruction).
 *
 * Optimistic by design (task-10-brief.md Step 2: "dragging a row and waiting
 * for a round trip before it moves is the interaction this whole dependency
 * exists to avoid"): the cache is set to the moved order BEFORE the request,
 * via `QueryClient.setQueryData` on the exact key `listsQueryOptions` reads
 * (queries.ts's `queryKeys.lists`) -- not a bespoke local "optimistic" flag,
 * so every consumer of the query sees the same moved row immediately.
 *
 * THREE OUTCOMES, three different cache writes:
 *  - success: the cache becomes the server's OWN rows (fresh `updatedAt`,
 *    real positions), then the map's usual invalidation runs too.
 *  - `409 order_mismatch`: the cache is replaced from the server's own
 *    `expected` id order -- never a retry, the server sent the truth
 *    (task-10-brief.md Step 2, verbatim).
 *  - anything else (network, 5xx): rolled back to the snapshot taken before
 *    the optimistic write, via `QueryClient`'s own cache rather than a
 *    bespoke "previous" variable threaded through JSX.
 */
export async function reorderListsAction(
  currentLists: readonly ListEntry[],
  from: number,
  to: number,
  io: ReorderListsIO,
): Promise<void> {
  if (from === to) return;
  const byId = new Map(currentLists.map((l) => [l.id, l]));
  const previous = io.queryClient.getQueryData<{ lists: ListEntry[] }>(queryKeys.lists);

  try {
    // MOVED INSIDE THE TRY (review Minor-1): `ListsPanel`'s `rows` is read
    // fresh from the query cache on every render, but `from`/`to` were
    // captured at `dragstart`/`drop` time. If a create/rename/delete/refetch
    // changes the row count in between, `from` or `to` can be stale and
    // out-of-range for THIS `currentLists`, and `moveItem` throws a
    // RangeError. Left outside the `try`, that throw becomes an unhandled
    // promise rejection (the call site does `void reorderListsAction(...)`
    // with no `.catch()`) and the drag silently does nothing -- no error
    // shown, nothing written. Inside the `try`, it is reported the same way
    // any other failed write is.
    const nextIds = moveItem(currentLists.map((l) => l.id), from, to);
    const optimistic = nextIds.map((id) => byId.get(id)).filter((l): l is ListEntry => l !== undefined);

    io.setError(null);
    io.queryClient.setQueryData(queryKeys.lists, { lists: optimistic });

    const result = await io.reorderLists(nextIds);
    io.queryClient.setQueryData(queryKeys.lists, { lists: result.lists });
    invalidateMutation(io.queryClient, 'reorderLists');
  } catch (error) {
    if (error instanceof RangeError) {
      // A stale drag: nothing was ever written optimistically (the throw
      // happened before `setQueryData`), so there is nothing to roll back --
      // only the operator to tell.
      io.setError(t('error.list_changed'));
      return;
    }
    if (error instanceof ApiError && error.code === 'order_mismatch') {
      const expectedIds = Array.isArray(error.body.expected)
        ? (error.body.expected as unknown[]).filter((x): x is string => typeof x === 'string')
        : [];
      const expectedRows = expectedIds.map((id) => byId.get(id)).filter((l): l is ListEntry => l !== undefined);
      io.queryClient.setQueryData(queryKeys.lists, { lists: expectedRows });
      io.setError(t('error.order_mismatch'));
      return; // no retry -- the server's `expected` is the new truth
    }
    if (previous) io.queryClient.setQueryData(queryKeys.lists, previous);
    io.setError(listErrorMessage(error));
  }
}

// ── settings ─────────────────────────────────────────────────────────────────

/**
 * `keys.startsWith('datev_')` IS THE WHOLE GROUPING RULE -- deliberately not a
 * hardcoded 14-name list. `GET /admin-api/settings` already answers `keys`
 * (D9: the server's own registry), and task-10-brief.md Step 3 is explicit
 * that "the form iterates the server's list and holds no key list of its
 * own." A prefix split keeps that promise: it groups whatever the server
 * actually sends, including a key nobody has thought to hardcode yet, rather
 * than silently dropping it.
 */
export function settingsGroups(keys: readonly string[]): { company: string[]; datev: string[] } {
  return {
    company: keys.filter((k) => !k.startsWith('datev_')),
    datev: keys.filter((k) => k.startsWith('datev_')),
  };
}

/**
 * Literal, presentation-only labels for the 14 keys the registry currently
 * defines (api/src/adminRoutes.ts:666-681) -- NOT a second copy of the key
 * list `settingsGroups` reads from the server: an unrecognised key still
 * renders (as its own raw name), it is just unlabelled, which is visibly odd
 * rather than silently dropped. The literal `t()` calls are what let
 * test/i18nKeys.test.ts prove every label string actually exists.
 */
export function settingsFieldLabel(key: string): string {
  switch (key) {
    case 'company_name': return t('masterData.settings.field.companyName');
    case 'company_address': return t('masterData.settings.field.companyAddress');
    case 'company_email': return t('masterData.settings.field.companyEmail');
    case 'timezone': return t('masterData.settings.field.timezone');
    case 'datev_berater_nr': return t('masterData.settings.field.datevBeraterNr');
    case 'datev_mandant_nr': return t('masterData.settings.field.datevMandantNr');
    case 'datev_wj_beginn': return t('masterData.settings.field.datevWjBeginn');
    case 'datev_sachkontenlaenge': return t('masterData.settings.field.datevSachkontenlaenge');
    case 'datev_clearing_konto': return t('masterData.settings.field.datevClearingKonto');
    case 'datev_erloeskonto_19': return t('masterData.settings.field.datevErloeskonto19');
    case 'datev_erloeskonto_7': return t('masterData.settings.field.datevErloeskonto7');
    case 'datev_bu_schluessel_19': return t('masterData.settings.field.datevBuSchluessel19');
    case 'datev_bu_schluessel_7': return t('masterData.settings.field.datevBuSchluessel7');
    case 'datev_gebuhr_konto': return t('masterData.settings.field.datevGebuhrKonto');
    default: return key;
  }
}

/** `timezone`'s empty-value placeholder: `''` deletes the row and restores
 *  the projection's OWN default, which is `Europe/Berlin` (plan 2a), never
 *  "nothing" (task-10-brief.md Step 3). Not translated: an IANA zone name is
 *  not German prose. */
export function settingsFieldPlaceholder(key: string): string | undefined {
  return key === 'timezone' ? 'Europe/Berlin' : undefined;
}

/**
 * `PUT` sends only CHANGED keys (task-10-brief.md Step 3). Diffing here, not
 * at the call site, is what makes "did this actually change?" a value a Node
 * test can assert instead of a JSX comparison a mutation could invert (e.g.
 * comparing `draft` to itself, which would always send everything or
 * nothing).
 */
export function changedSettings(
  original: Record<string, string>,
  draft: Record<string, string>,
): Record<string, string> {
  const changed: Record<string, string> = {};
  for (const [key, value] of Object.entries(draft)) {
    if ((original[key] ?? '') !== value) changed[key] = value;
  }
  return changed;
}

export interface SaveSettingsIO extends MutationShell {
  putSettings: (settings: Record<string, string>) => Promise<SettingsResponse>;
  onSaved: (settings: SettingsResponse) => void;
}

/** Nothing changed -> nothing sent -> no busy flicker, no audit row for a
 *  save that wrote zero keys. */
export async function saveSettingsAction(
  original: Record<string, string>,
  draft: Record<string, string>,
  io: SaveSettingsIO,
): Promise<boolean> {
  const changed = changedSettings(original, draft);
  if (Object.keys(changed).length === 0) return false;
  return runMasterDataMutation(
    'putSettings',
    () => io.putSettings(changed),
    io,
    { onSuccess: (result) => io.onSaved(result), resolveError: settingsErrorMessage },
  );
}

// ── features (exactly two self-serve flags, D10) ────────────────────────────

export type SelfServeFeature = 'ebay' | 'priceScan';

export interface ToggleFeatureIO extends MutationShell {
  patchFeatures: (json: { ebay?: boolean; priceScan?: boolean }) => Promise<FeaturesResponse>;
}

/**
 * EXACTLY ONE FLAG, EXACTLY THE VALUE THE TOGGLE NOW SHOWS. `PATCH
 * /admin-api/features` takes `{ ebay?, priceScan? }` (D10); the object sent
 * here has ONE key, computed from the two arguments the call site passed in,
 * never read back off `Features` state that could be stale or off the wrong
 * field. This is the concrete shape of the controller's second named risk:
 * "a feature toggle that writes the opposite value or targets the wrong
 * flag" -- both halves are arguments, both are asserted in Node.
 *
 * `photos`/`enrichment` have NO action here at all: the merged handler
 * refuses them with `400 not_self_serve` (task-10-brief.md Step 4), so there
 * is nothing for this function to accept for them by construction, not by a
 * runtime check.
 */
export async function toggleSelfServeFeatureAction(
  flag: SelfServeFeature,
  next: boolean,
  io: ToggleFeatureIO,
): Promise<boolean> {
  return runMasterDataMutation(
    'patchFeatures',
    () => io.patchFeatures({ [flag]: next }),
    io,
    { resolveError: featuresErrorMessage },
  );
}
